import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { homeSecurityInfoService } from '../../../../services/homeSecurityInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { useUserSelection } from '../../../../contexts/UserSelectionContext';
const logger = createLogger('HomeSecurityData');

type InfoItemProps = {
  label: string;
  value: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value || 'Not specified'}</Text>
    <View style={styles.separator} />
  </View>
);

type RouteParams = {
  taskId: number;
  targetUserId?: string;
  viewOnly?: boolean;
};

export const HomeSecurityDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<{ params: RouteParams }, 'params'>>();
  const { targetUserId, viewOnly } = route.params || {};
  // K5: a successor sees the owner's logins once the death is verified.
  const { isDeathVerifiedForSelectedUser } = useUserSelection();
  const [homeSecurityData, setHomeSecurityData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUsername, setShowUsername] = useState<{ [key: string]: boolean }>({});
  const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({});
  const [showMasterCode, setShowMasterCode] = useState<{ [key: string]: boolean }>({});
  const [showUserCodes, setShowUserCodes] = useState<{ [key: string]: boolean }>({});
  const [showAccountNumber, setShowAccountNumber] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadHomeSecurityData();
  }, []);

  const loadHomeSecurityData = async () => {
    try {
      setLoading(true);
      const data = await homeSecurityInfoService.getAllHomeSecurityInfo(targetUserId);

      if (data && data.length > 0) {
        // Load portal credentials for each home security system
        const updatedData = await Promise.all(
          data.map(async (homeSecurity) => {
            // Skip loading portal credentials in viewOnly mode - they are protected until EOL
            if (!viewOnly || isDeathVerifiedForSelectedUser) {
              try {
                logger.debug('[Portal Debug] Attempting to load portal credentials for home security ID:', homeSecurity.id);
                const portalData = await vaultService.getPortalCredentials(homeSecurity.id, 'home-security', targetUserId);
                logger.debug('[Portal Debug] Portal data retrieved for home security:', homeSecurity.id, portalData);
                if (portalData) {
                  homeSecurity.username = portalData.username;
                  homeSecurity.password = portalData.password;
                  homeSecurity.accountNumber = portalData.accountNumber;
                  // Alarm codes live in the encrypted record too (K5, D5).
                  if (portalData.masterCode) homeSecurity.masterCode = portalData.masterCode;
                  if (portalData.userCodes) homeSecurity.userCodes = portalData.userCodes;
                  logger.debug('[Portal Debug] Assigned credentials to home security data');
                } else {
                  logger.debug('[Portal Debug] No portal data returned for home security:', homeSecurity.id);
                }
              } catch (vaultError) {
                logger.debug('[Portal Debug] Error loading portal data for home security:', homeSecurity.id, vaultError);
              }
            }
            return homeSecurity;
          })
        );

        logger.debug('[Data Debug] Final home security data being set:', updatedData.length, 'systems');
        logger.debug('[Data Debug] First system account number:', updatedData[0]?.accountNumber);
        setHomeSecurityData(updatedData);
      }
    } catch (error) {
      logger.error('Error loading home security data:', error);
      Alert.alert('Error', 'Failed to load home security information');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatCurrency = (amount: string | number) => {
    if (!amount) return '';
    if (typeof amount === 'number') {
      return `$${amount.toFixed(2)}`;
    }
    return amount.toString();
  };

  const handleEditHomeSecurity = (homeSecurity: any) => {
    navigation.navigate('HomeSecurityInput', { 
      homeSecurityId: homeSecurity.id, 
      isEdit: true 
    });
  };

  const handleDeleteHomeSecurity = async (homeSecurity: any) => {
    Alert.alert('Delete', 'Are you sure you want to delete this home security information?', [
      { text: 'Cancel', style: 'cancel' },
      { 
        text: 'Delete', 
        style: 'destructive', 
        onPress: async () => {
          try {
            await homeSecurityInfoService.deleteHomeSecurityInfo(homeSecurity.id);
            
            // Reload data after deletion
            await loadHomeSecurityData();
            
            Alert.alert('Deleted', 'Home security information deleted.');
          } catch (e) {
            logger.error('Delete home security info failed:', e);
            Alert.alert('Error', 'Failed to delete home security information');
          }
        } 
      },
    ]);
  };

  const handleAddHomeSecurity = () => {
    navigation.navigate('HomeSecurityInput');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Home Security Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Loading home security information...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!homeSecurityData || homeSecurityData.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Home Security Information</Text>

        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}

        <View style={styles.whiteContainer}>
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>
              {viewOnly
                ? "This user has not added home security information yet."
                : "No home security information found"}
            </Text>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addButton}
                onPress={handleAddHomeSecurity}
              >
                <Text style={styles.addButtonText}>Add Home Security Info</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Home Security Information</Text>

      {viewOnly && (
        <View style={styles.viewOnlyBanner}>
          <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
        </View>
      )}

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {homeSecurityData.map((homeSecurity, index) => (
            <View key={homeSecurity.id || index} style={styles.homeSecurityCard}>
              <View style={styles.homeSecurityHeader}>
                <View style={{flex: 1, flexShrink: 1}}>
                  <Text style={styles.homeSecurityName}>
                    {homeSecurity.companyName || 'Unknown Provider'}
                  </Text>
                  <Text style={styles.homeSecurityType}>
                    {homeSecurity.systemType || 'Not specified'}
                  </Text>
                </View>
                {!viewOnly && (
                  <View style={styles.cardButtons}>
                    <TouchableOpacity
                      style={styles.editHomeSecurityButton}
                      onPress={() => handleEditHomeSecurity(homeSecurity)}
                    >
                      <Text style={styles.editHomeSecurityButtonText}>Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deleteHomeSecurityButton}
                      onPress={() => handleDeleteHomeSecurity(homeSecurity)}
                    >
                      <Text style={styles.deleteHomeSecurityButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
              
              <InfoItem label="Company Name" value={homeSecurity.companyName || ''} />
              <InfoItem label="System Type" value={homeSecurity.systemType || ''} />
              
              {homeSecurity.accountNumber && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Account Number 🔒</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showAccountNumber[homeSecurity.id] ? homeSecurity.accountNumber : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowAccountNumber(prev => ({ ...prev, [homeSecurity.id]: !prev[homeSecurity.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showAccountNumber[homeSecurity.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              {homeSecurity.masterCode && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Master Code 🔒</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showMasterCode[homeSecurity.id] ? homeSecurity.masterCode : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowMasterCode(prev => ({ ...prev, [homeSecurity.id]: !prev[homeSecurity.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showMasterCode[homeSecurity.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              {homeSecurity.userCodes && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>User Codes 🔒</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showUserCodes[homeSecurity.id] ? homeSecurity.userCodes : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowUserCodes(prev => ({ ...prev, [homeSecurity.id]: !prev[homeSecurity.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showUserCodes[homeSecurity.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              <InfoItem label="Service Address" value={homeSecurity.serviceAddress || ''} />
              <InfoItem label="Monitoring Station Phone" value={formatPhoneNumber(homeSecurity.monitoringStationPhone) || ''} />
              <InfoItem label="Customer Service Phone" value={formatPhoneNumber(homeSecurity.customerServicePhone) || ''} />
              <InfoItem label="Website" value={homeSecurity.website || ''} />
              <InfoItem label="App Name" value={homeSecurity.appName || ''} />
              <InfoItem label="Monthly Cost" value={formatCurrency(homeSecurity.monthlyCost) || ''} />
              <InfoItem label="Autopay Enabled" value={homeSecurity.autopayEnabled ? 'Yes' : 'No'} />
              <InfoItem label="Contract End Date" value={homeSecurity.contractEndDate || ''} />
              <InfoItem label="Installation Date" value={homeSecurity.installationDate || ''} />
              <InfoItem label="Warranty Info" value={homeSecurity.warrantyInfo || ''} />
              
              {homeSecurity.username && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Portal Username</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showUsername[homeSecurity.id] ? homeSecurity.username : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowUsername(prev => ({ ...prev, [homeSecurity.id]: !prev[homeSecurity.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showUsername[homeSecurity.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              {homeSecurity.password && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Portal Password</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showPassword[homeSecurity.id] ? homeSecurity.password : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowPassword(prev => ({ ...prev, [homeSecurity.id]: !prev[homeSecurity.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showPassword[homeSecurity.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              <InfoItem label="Notes" value={homeSecurity.notes || ''} />
            </View>
          ))}
          
          <View style={styles.buttonContainer}>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addAnotherHomeSecurityButton}
                onPress={handleAddHomeSecurity}
              >
                <Text style={styles.addAnotherHomeSecurityButtonText}>Add Another Home Security System</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.button, styles.backToListButton]}
              onPress={() => navigation.navigate('PropertyChecklist')}
            >
              <Text style={styles.buttonText}>Back to Checklist</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginTop: ms(10),
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  homeSecurityCard: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  homeSecurityHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(20),
    paddingBottom: ms(15),
    borderBottomWidth: ms(2),
    borderBottomColor: '#29BFC0',
  },
  homeSecurityName: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  homeSecurityType: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  cardButtons: {
    flexDirection: 'row',
    gap: ms(8),
  },
  editHomeSecurityButton: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  editHomeSecurityButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  deleteHomeSecurityButton: {
    backgroundColor: '#DC3545',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  deleteHomeSecurityButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  infoItem: {
    marginBottom: ms(15),
  },
  label: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(5),
  },
  value: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  eyeButton: {
    padding: ms(5),
    marginLeft: ms(10),
  },
  eyeIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#6B7280',
  },
  separator: {
    height: s(1),
    backgroundColor: '#E5E5E5',
  },
  buttonContainer: {
    padding: ms(20),
    paddingBottom: ms(120),
    gap: ms(15),
  },
  addAnotherHomeSecurityButton: {
    backgroundColor: '#F2994A',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addAnotherHomeSecurityButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  button: {
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
}); 